import { useEffect, useRef, useState } from "react";
import {
  Bars3Icon,
  BellIcon,
  ChevronDownIcon,
  GiftIcon,
  MagnifyingGlassIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";
import { signOut } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, doc, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { auth } from "../firebase";
import { db } from "../firebase";
import BonusModal from "./BonusModal";
import NotificationsDropdown from "./NotificationsDropdown";
import { getTotalVisibleBonus } from "../lib/affiliateBonus";

export default function Navbar({
  isAuthenticated,
  balance,
  onSidebarToggle,
  user,
  invites = [],
  handleAccept,
  handleDecline,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [lastNotifId, setLastNotifId] = useState(null);
  const notifRef = useRef(null);
  const dropdownRef = useRef(null);
  const notifSound = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const lid = new URLSearchParams(location.search).get("lid") || "";

  useEffect(() => {
    notifSound.current = new Audio("/sounds/notification.mp3");
    notifSound.current.volume = 1.0;
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return onSnapshot(doc(db, "users", user.uid), (snapshot) => {
      if (snapshot.exists()) {
        setUserProfile(snapshot.data());
      }
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const notificationsQuery = query(
      collection(db, "notifications"),
      where("userId", "==", user.uid),
      orderBy("timestamp", "desc")
    );
    return onSnapshot(notificationsQuery, (snapshot) => {
      const nextNotifications = snapshot.docs.map((entry) => ({
        id: entry.id,
        ...entry.data(),
      }));
      setNotifications(nextNotifications);

      if (nextNotifications.length && nextNotifications[0].id !== lastNotifId) {
        notifSound.current?.play().catch(() => {});
        setLastNotifId(nextNotifications[0].id);
      }
    });
  }, [lastNotifId, user?.uid]);

  useEffect(() => {
    function handleClick(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifDropdown(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const displayBalance = Number(
    typeof balance !== "undefined" ? balance : userProfile?.balance || 0
  ).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const displayBonus = getTotalVisibleBonus(userProfile || {}).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const totalCount =
    invites.length + notifications.filter((notification) => notification.read === false).length;

  return (
    <>
      <header className="fixed left-0 right-0 top-0 z-50 border-b border-[#242a34] bg-[rgba(18,21,28,0.94)] px-3 py-3 backdrop-blur-xl md:pl-[212px]">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                className="rounded-2xl bg-[#1c2129] p-2 text-white md:hidden"
                onClick={onSidebarToggle}
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <div className="hidden items-center gap-2 md:flex">
                <TopTab label="Casino" active onClick={() => navigate("/")} />
                <TopTab label="Sports" active={location.pathname.startsWith("/sports")} onClick={() => navigate("/sports")} />
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isAuthenticated ? (
                <>
                  <button
                    type="button"
                    onClick={() => navigate("/deposit")}
                    className="rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-black text-[#07110d] transition hover:bg-emerald-400"
                  >
                    Deposit
                  </button>
                  <button
                    type="button"
                    onClick={() => setBonusModalOpen(true)}
                    className="hidden items-center gap-2 rounded-2xl bg-[#1c2129] px-4 py-2 text-sm font-semibold text-white md:flex"
                  >
                    <WalletIcon className="h-5 w-5 text-emerald-300" />
                    {displayBalance} USDT
                    <span className="rounded-full bg-yellow-400/15 px-2 py-1 text-[11px] font-bold text-yellow-200">
                      +{displayBonus}
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => navigate("/login" + (lid ? `?lid=${lid}` : ""))}
                    className="rounded-2xl bg-[#1c2129] px-4 py-2 text-sm font-semibold text-white"
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/register" + (lid ? `?lid=${lid}` : ""))}
                    className="rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-black text-[#07110d]"
                  >
                    Sign up
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[220px] flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
              <input
                readOnly
                value=""
                placeholder="Game name | Provider"
                className="w-full rounded-[20px] border border-[#252b35] bg-[#171b22] py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500"
              />
            </div>

            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <div className="rounded-[18px] bg-[#1c2129] px-4 py-2 text-sm font-semibold text-white md:hidden">
                  {displayBalance} USDT
                </div>

                <div className="relative" ref={notifRef}>
                  <button
                    className="relative rounded-2xl bg-[#1c2129] p-3 text-white"
                    onClick={() => setShowNotifDropdown((current) => !current)}
                  >
                    <BellIcon className="h-5 w-5" />
                    {totalCount > 0 ? (
                      <span className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        {totalCount}
                      </span>
                    ) : null}
                  </button>
                  {showNotifDropdown ? (
                    <div className="absolute right-0 mt-2 z-50">
                      <NotificationsDropdown
                        invites={invites}
                        notifications={notifications}
                        user={user}
                        onAccept={handleAccept}
                        onDecline={handleDecline}
                        onClose={() => setShowNotifDropdown(false)}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowDropdown((current) => !current)}
                    className="flex items-center gap-2 rounded-2xl bg-[#1c2129] px-3 py-2 text-white"
                  >
                    {user?.photoURL ? (
                      <img src={user.photoURL} alt="Profile" className="h-8 w-8 rounded-full" />
                    ) : (
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 font-bold">
                        {user?.displayName?.charAt(0) || "U"}
                      </span>
                    )}
                    <ChevronDownIcon className="h-4 w-4 text-slate-400" />
                  </button>
                  {showDropdown ? (
                    <div className="absolute right-0 mt-2 w-52 overflow-hidden rounded-[22px] border border-[#252b35] bg-[#171b22] shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
                      <div className="border-b border-[#252b35] px-4 py-3 text-sm text-slate-200">
                        {user?.displayName || user?.email}
                      </div>
                      <button
                        onClick={() => {
                          navigate("/profile");
                          setShowDropdown(false);
                        }}
                        className="w-full px-4 py-3 text-left text-sm text-slate-300 transition hover:bg-white/[0.05]"
                      >
                        Profile
                      </button>
                      <button
                        onClick={() => {
                          signOut(auth);
                          setShowDropdown(false);
                          navigate("/");
                          window.location.reload();
                        }}
                        className="w-full px-4 py-3 text-left text-sm text-red-300 transition hover:bg-red-500/10"
                      >
                        Logout
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {bonusModalOpen ? (
        <BonusModal user={userProfile} onClose={() => setBonusModalOpen(false)} />
      ) : null}
    </>
  );
}

function TopTab({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
        active
          ? "bg-[#20252d] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
          : "text-slate-400 hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}
